/**
 * Cache Service
 * 
 * Utilities for querying cached odds and fixtures with automatic fallback to live API
 */

import Constants from 'expo-constants';

// Get functions base URL
import { TEAM_LOOKUP_MAP, TEAM_ALIASES, TEAM_TO_LEAGUE } from './teamResolution';
import { parseQueryEntities } from './queryEntities';

// Cache lookups are shortcuts in front of nlOdds. A stalled one must fail fast
// so the search falls through instead of sitting on "Searching for bets…".
export const CACHE_LOOKUP_TIMEOUT_MS = 20000;

/**
 * GET JSON with a hard deadline that covers the body read too, and an optional
 * caller signal (the Cancel button). Returns { ok, status, statusText, data }.
 */
export async function fetchJsonWithTimeout(url, init = {}, { timeoutMs = CACHE_LOOKUP_TIMEOUT_MS, signal = null } = {}) {
  const ctrl = new AbortController();
  const timer = setTimeout(() => ctrl.abort(), timeoutMs);
  const onCancel = () => ctrl.abort();
  if (signal?.aborted) onCancel();
  signal?.addEventListener?.('abort', onCancel);
  try {
    const response = await fetch(url, { ...init, signal: ctrl.signal });
    const data = response.ok ? await response.json() : null;
    return { ok: response.ok, status: response.status, statusText: response.statusText, data };
  } finally {
    clearTimeout(timer);
    signal?.removeEventListener?.('abort', onCancel);
  }
}

const getFunctionsBaseUrl = () => {
  // Check if localhost
  const hostname = typeof window !== 'undefined' ? window.location.hostname : 'unknown';
  const isLocalhost = hostname === 'localhost' || hostname === '127.0.0.1';
  
  // ⭐ Only use emulator if explicitly enabled via env var or query param
  // This prevents 0-result errors when emulator isn't running
  const useEmulator = isLocalhost && (
    (typeof process !== 'undefined' && process?.env?.USE_EMULATOR === 'true') ||
    (typeof window !== 'undefined' && window.location?.search?.includes('emulator=true'))
  );
  
  console.log(`[CacheService] 🌐 getFunctionsBaseUrl: hostname="${hostname}", isLocalhost=${isLocalhost}, useEmulator=${useEmulator}`);
  
  if (useEmulator) {
    console.log('[CacheService] 🔧 Using EMULATOR: http://127.0.0.1:5001/bet-optimized-fe922/us-central1');
    return 'http://127.0.0.1:5001/bet-optimized-fe922/us-central1'; // Emulator
  }
  
  const prodUrl = (
    (typeof process !== 'undefined' && process?.env?.EXPO_PUBLIC_FUNCTIONS_BASE_URL) ||
    (Constants?.expoConfig?.extra?.EXPO_PUBLIC_FUNCTIONS_BASE_URL) ||
    (Constants?.expoConfig?.extra?.FUNCTIONS_BASE_URL) ||
    (Constants?.manifest?.extra?.FUNCTIONS_BASE_URL) ||
    'https://us-central1-bet-optimized-fe922.cloudfunctions.net' // Production
  );
  console.log(`[CacheService] 🌐 Using PRODUCTION: ${prodUrl}`);
  return prodUrl;
};

/**
 * Query cached fixtures
 * @param {Object} params - Query parameters
 * @param {string} params.league - League (e.g., 'nba', 'nfl')
 * @param {string} params.sport - Sport (e.g., 'basketball', 'football')
 * @param {string} params.team - Team name
 * @param {string} params.player - Player name
 * @param {string} params.market - Market name
 * @param {number} params.limit - Max results
 * @param {number} params.maxAgeMinutes - Max cache age in minutes
 * @returns {Promise<{success: boolean, cached: boolean, fixtures: Array, message: string}>}
 */
export async function queryCachedFixtures(params = {}) {
  try {
    const {
      league,
      sport,
      team,
      player,
      market,
      limit = 50,
      maxAgeMinutes = 60
    } = params;
    
    const base = getFunctionsBaseUrl();
    const queryParams = new URLSearchParams();
    
    if (league) queryParams.append('league', league);
    if (sport) queryParams.append('sport', sport);
    if (team) queryParams.append('team', team);
    if (player) queryParams.append('player', player);
    if (market) queryParams.append('market', market);
    if (limit) queryParams.append('limit', limit.toString());
    if (maxAgeMinutes) queryParams.append('max_age_minutes', maxAgeMinutes.toString());
    
    const url = `${base}/queryCachedFixtures?${queryParams.toString()}`;
    console.log('[CacheService] Querying cached fixtures:', url);
    
    const response = await fetchJsonWithTimeout(url, {
      method: 'GET',
      headers: { 'Content-Type': 'application/json' }
    });
    
    if (!response.ok) {
      throw new Error(`HTTP ${response.status}: ${response.statusText}`);
    }
    
    const data = response.data;
    console.log('[CacheService] Cache query result:', {
      cached: data.cached,
      fixtures: data.fixtures?.length || 0
    });
    
    return data;
  } catch (error) {
    console.error('[CacheService] Error querying cached fixtures:', error);
    return {
      success: false,
      cached: false,
      fixtures: [],
      message: `Error: ${error.message}`
    };
  }
}

/**
 * Query cached SGPs
 * @param {Object} params - Query parameters
 * @param {string} params.league - League (e.g., 'nba', 'nfl')
 * @param {string} params.sport - Sport (e.g., 'basketball', 'football')
 * @param {string} params.team - Team name
 * @param {string} params.fixture_id - Specific fixture ID
 * @param {number} params.limit - Max results
 * @param {number} params.max_age_hours - Max cache age in hours (default: 6)
 * @param {string} params.sort_by - Sort by field (e.g., 'ev', 'odds')
 * @returns {Promise<{success: boolean, sgps: Array, message: string}>}
 */
export async function queryCachedSGPs(params = {}) {
  try {
    const {
      league,
      sport,
      team,
      fixture_id,
      limit = 100,
      max_age_hours = 6,
      sort_by = 'ev',
      signal = null,
    } = params;
    
    const base = getFunctionsBaseUrl();
    const queryParams = new URLSearchParams();
    
    // Only add filters if provided - this allows old SGPs without league/sport to be returned
    if (league) queryParams.append('league', league);
    if (sport) queryParams.append('sport', sport);
    if (team) queryParams.append('team', team);
    if (fixture_id) queryParams.append('fixture_id', fixture_id);
    queryParams.append('limit', limit.toString());
    queryParams.append('max_age_hours', max_age_hours.toString());
    queryParams.append('sort_by', sort_by);
    
    const url = `${base}/queryCachedSGPs?${queryParams.toString()}`;
    console.log('[CacheService] Querying cached SGPs:', url);
    
    const response = await fetchJsonWithTimeout(url, {
      method: 'GET',
      headers: { 'Content-Type': 'application/json' }
    }, { signal });
    
    if (!response.ok) {
      throw new Error(`HTTP ${response.status}: ${response.statusText}`);
    }
    
    const data = response.data;
    console.log('[CacheService] SGP cache query result:', {
      success: data.success,
      sgps: data.sgps?.length || 0,
      fixtures: data.fixtures?.length || 0,
      totalAvailable: data.total_available
    });
    
    return data;
  } catch (error) {
    console.error('[CacheService] Error querying cached SGPs:', error);
    return {
      success: false,
      sgps: [],
      fixtures: [],
      message: `Error: ${error.message}`
    };
  }
}

/**
 * Get cached odds for a query
 * @param {Object} params - Query parameters
 * @param {string} params.league - League
 * @param {string} params.sport - Sport
 * @param {string} params.team - Team name
 * @param {string} params.player - Player name
 * @param {string} params.market - Market name
 * @param {string} params.date - Date (YYYY-MM-DD)
 * @param {number} params.maxAgeMinutes - Max cache age
 * @returns {Promise<{success: boolean, cached: boolean, odds: Array}>}
 */
export async function getCachedOdds(params = {}) {
  try {
    const {
      league,
      sport,
      team,
      player,
      market,
      date,
      maxAgeMinutes = 60
    } = params;
    
    const base = getFunctionsBaseUrl();
    const url = `${base}/getCachedOdds`;
    
    console.log('[CacheService] Getting cached odds:', params);
    
    const response = await fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        league,
        sport,
        team,
        player,
        market,
        date,
        max_age_minutes: maxAgeMinutes
      })
    });
    
    if (!response.ok) {
      throw new Error(`HTTP ${response.status}: ${response.statusText}`);
    }
    
    const data = await response.json();
    console.log('[CacheService] Cached odds result:', {
      cached: data.cached,
      odds: data.odds?.length || 0
    });
    
    return data;
  } catch (error) {
    console.error('[CacheService] Error getting cached odds:', error);
    return {
      success: false,
      cached: false,
      odds: [],
      message: `Error: ${error.message}`
    };
  }
}

/**
 * Get cache statistics and health
 * @returns {Promise<Object>}
 */
export async function getCacheStats() {
  try {
    const base = getFunctionsBaseUrl();
    const url = `${base}/getCacheStats`;
    
    const response = await fetch(url, {
      method: 'GET',
      headers: { 'Content-Type': 'application/json' }
    });
    
    if (!response.ok) {
      throw new Error(`HTTP ${response.status}: ${response.statusText}`);
    }
    
    return await response.json();
  } catch (error) {
    console.error('[CacheService] Error getting cache stats:', error);
    return {
      success: false,
      cache_status: 'error',
      error: error.message
    };
  }
}

/**
 * 🧠 Smart query parser - extracts entities from natural language
 * 
 * NOTE: This is a HELPER for the cache-first flow. The AI (nlOdds) does the heavy lifting
 * for complex parsing. This parser just helps with:
 * 1. Obvious team/market detection (to avoid cache misses)
 * 2. Preventing false positives (e.g., "Lakers Moneyline" as player name)
 * 3. Quick client-side filtering before API calls
 * 
 * The CACHE is the source of truth for:
 * - Available leagues/sports
 * - Available markets
 * - Available odds/SGPs
 * - Team rosters (which league a team is in)
 * 
 * If this parser misses something, the AI will catch it. If the AI misses something,
 * the cache search will fall back gracefully.
 * 
 * @param {string} prompt - Natural language query
 * @returns {Object} - Extracted entities
 */
/**
 * 🚀 OPTIMIZED TEAM LOOKUP SYSTEM
 * 
 * Features:
 * 1. O(1) Map lookups (instead of O(n) array iteration)
 * 2. City-to-teams mapping for location queries
 * 3. Fuzzy matching for typo tolerance
 * 4. Context-aware disambiguation (season, market type)
 * 5. Aliases and nickname support
 * 6. Confidence scoring for ambiguous matches
 */


// 🚀 Initialize the O(1) lookup Map after TEAM_TO_LEAGUE is defined
(function initializeTeamLookupMap() {
  for (const [teamName, info] of Object.entries(TEAM_TO_LEAGUE)) {
    const teamLower = teamName.toLowerCase();
    TEAM_LOOKUP_MAP.set(teamLower, { 
      ...info, 
      canonical: teamName 
    });
  }
  
  // Also add aliases to the lookup map
  for (const [alias, canonical] of Object.entries(TEAM_ALIASES)) {
    const aliasLower = alias.toLowerCase();
    if (!TEAM_LOOKUP_MAP.has(aliasLower) && TEAM_TO_LEAGUE[canonical]) {
      TEAM_LOOKUP_MAP.set(aliasLower, {
        ...TEAM_TO_LEAGUE[canonical],
        canonical,
        isAlias: true
      });
    }
  }
  
})();

/**
 * 🔍 Fuzzy team name matching for typos and variations
 * Uses Levenshtein distance to find close matches
 * @param {string} input - The team name to match
 * @returns {{match: string, confidence: number}|null} - Best match or null
 */

/**
 * Query with cache-first strategy and automatic fallback to live API
 * @param {string} prompt - Natural language query
 * @param {Object} options - Query options
 * @param {Function} onCacheStatus - Callback for cache status updates
 * @returns {Promise<Object>} - Query results
 */

function synthesizeClassification(entities) {
  const { queryType, player, team, market, line, direction, league, sport } = entities || {};
  let type = 'GENERIC';
  if (player) {
    type = market ? 'SPECIFIC_PLAYER_MARKET' : 'PLAYER_PROP';
  } else if (team && market) {
    type = 'SPECIFIC_TEAM_MARKET';
  } else if (team) {
    type = 'TEAM_MARKETS';
  } else if (league || sport) {
    type = 'LEAGUE_QUERY';
  }
  return {
    type,
    entities: {
      player: player || null,
      team: team || null,
      market: market || null,
      lineThreshold: line ?? null,
      lineDirection: direction || null,
      spreadLine: direction ? null : line ?? null,
      league: league || null,
      sport: sport || null,
    },
    confidence: 0.7,
    _synthesizedFromCache: true,
  };
}

function buildQueryResolution(entities, originalPrompt) {
  const parts = [];
  if (entities.player) parts.push(entities.player);
  if (entities.team) parts.push(entities.team);
  if (entities.direction && entities.line != null) parts.push(`${entities.direction} ${entities.line}`);
  else if (entities.line != null) parts.push(String(entities.line));
  if (entities.market) {
    const clean = entities.market.replace(/[-_]/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
    parts.push(clean);
  }
  const resolved = parts.length > 0 ? parts.join(' ') : originalPrompt;
  return {
    original: originalPrompt,
    resolved,
    entities: {
      player: entities.player || null,
      team: entities.team || null,
      market: entities.market || null,
      line: entities.line ?? null,
      direction: entities.direction || null,
      league: entities.league || null,
    },
    confidence: 0.7,
    method: 'cache_synthesized',
  };
}

export async function queryWithCacheFallback(prompt, options = {}, onCacheStatus = null) {
  const {
    league,
    sport,
    team,
    player,
    market,
    prefer = 'straight',
    defaults = {},
    maxAgeMinutes = 60,
    isLive = false,
    // User tracking for analytics
    userId = null,
    userEmail = null,
    userName = null,
    // Caller's cancel signal (the Cancel button). Kept out of the request body.
    abortSignal = null,
  } = options;
  
  console.log('[CacheService] Starting cache-first query:', { prompt, options });
  
  // 🧠 Step 0: Parse query to extract entities
  const entities = parseQueryEntities(prompt);
  console.log('[CacheService] 🧠 Parsed entities:', entities);
  
  // Step 1: Try cache first using smart routing
  if (onCacheStatus) onCacheStatus({ 
    stage: 'checking_cache', 
    message: 'Checking cached data...' 
  });
  
  let cacheResult = null;
  
  // 🔧 FIX: Don't treat player prop queries as SGP queries even when prefer='sgpx'
  // If the user is searching for a specific player with a market/line, it's a straight bet, not an SGP
  const isPlayerPropQuery = entities.queryType === 'player' && entities.market && entities.line !== null;
  
  if (isPlayerPropQuery) {
    console.log('[CacheService] 🎯 Player prop query detected - treating as straight bet, not SGP:', {
      player: entities.player,
      market: entities.market,
      line: entities.line
    });
  }
  
  // For SGP queries, check SGP cache first, then odds cache
  // BUT skip SGP path if it's clearly a player prop query
  if ((prefer === 'sgp' || prefer === 'sgpx') && !isPlayerPropQuery) {
    console.log('[CacheService] SGP query detected - checking SGP cache...');
    
    // Try to intelligently extract fixture info from prompt
    // This is a simple heuristic - the NL parser would do better
    const fixturesResult = await queryCachedFixtures({
      league,
      sport,
      team,
      limit: 20,
      maxAgeMinutes
    });
    
    if (fixturesResult.cached && fixturesResult.fixtures?.length > 0) {
      console.log(`[CacheService] Found ${fixturesResult.fixtures.length} cached fixtures for SGP query`);
      
      // ⚡ NOTE: We skip direct Firestore SGP cache queries here because:
      // 1. Frontend doesn't have permission to read sgp_cache collection directly
      // 2. The HTTP function queryCachedSGPs is already called earlier in SgpFlowScreen
      // 3. If we got here, it means the HTTP function returned 0 SGPs, so no point re-checking
      console.log('[CacheService] Skipping direct SGP cache check (use HTTP function instead)');
      console.log('[CacheService] Fixtures found but SGP cache already checked via HTTP - will fall back to API');
    }
  }

  // Always call nlOdds — the backend classifyQuery() handles SGP vs straight detection
  console.log('[CacheService] ⚡ DIRECT TO BACKEND: Using nlOdds for sophisticated parsing');
    
    // Skip straight to the backend nlOdds call
    if (onCacheStatus) onCacheStatus({ 
      stage: 'checking_cache', 
      message: 'Searching...' 
    });
    
    try {
      const base = getFunctionsBaseUrl();
      
      // ⭐ Extract fixtureId from options if provided (e.g., from ticker click)
      const fixtureId = options.fixtureId || options.fixture_id || null;
      
      // ⭐⭐⭐ FIX: Send frontend entity parsing as HINTS alongside defaults
      // The backend's classifyQuery() is the authority for query classification.
      // Frontend entities are sent as `frontendHints` so the backend can use them
      // as supplementary context OR override them when its own detection is more confident.
      const body = {
        prompt,
        // Honor the caller's bet-type intent (sgp/sgpx/straight). Hardcoding
        // 'straight' here made nlOdds mis-classify SGP-mode queries whenever
        // the SGP cache shortcut missed.
        prefer: prefer || 'straight',
        // ⭐ Pass fixtureId for exact matchup filtering
        fixture_id: fixtureId,
        // ⭐ User tracking for query analytics
        userId,
        userEmail,
        userName,
        // ⭐ N2: suppress the clarify-or-abstain gate (background queries can't
        // render clarification chips — they take the legacy best-guess instead)
        ...(options.noClarify === true ? { noClarify: true } : {}),
        // ⭐ N3: suppress auto-broadening (the "Undo" path from the broadened-
        // results banner — user wants exact matches or an honest empty state)
        ...(options.noAutoBroaden === true ? { noAutoBroaden: true } : {}),
        defaults: {
          ...defaults,
          // ⭐ N1: frontend-PARSED sport/league must NOT constrain the backend —
          // they travel in frontendHints only. defaults keeps caller context
          // (screen league) which the backend may still override (v1.51 authority).
          // Do not write `league: undefined` — that wipes ticker/screen defaults.league.
          ...(sport || defaults.sport ? { sport: sport || defaults.sport } : {}),
          ...((league || defaults.league)
            ? { league: String(league || defaults.league).toLowerCase() }
            : {}),
          timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
          now: new Date().toISOString()
        },
        // ⭐ Frontend entity parsing sent as separate hints
        // Backend can compare its own detection against these and choose the best
        frontendHints: {
          parsedSport: entities.sport || null,
          parsedLeague: entities.league || null,
          parsedPlayer: entities.player || null,
          parsedTeam: entities.team || null,
          parsedMarket: entities.market || null,
          // ⭐ N1: line/direction were parsed but dropped — backend uses them
          // as a fallback when its own threshold detection finds nothing.
          parsedLine: (typeof entities.line === 'number' && Number.isFinite(entities.line)) ? entities.line : null,
          parsedDirection: entities.direction || null,
          queryType: entities.queryType || null
        }
      };
      
      console.log('[CacheService] ⚡ Calling /nlOdds directly with:', body);
      if (fixtureId) {
        console.log('[CacheService] 🎯 Including fixtureId for exact matchup filtering:', fixtureId);
      }
      
      const fetchOnce = async () => {
        const ctrl = new AbortController();
        const timer = setTimeout(() => ctrl.abort('Query timed out after 150 seconds'), 150000);
        const onCancel = () => ctrl.abort('Search cancelled');
        if (abortSignal?.aborted) onCancel();
        abortSignal?.addEventListener?.('abort', onCancel);
        try {
          return await fetch(`${base}/nlOdds`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(body),
            signal: ctrl.signal
          });
        } finally {
          clearTimeout(timer);
          abortSignal?.removeEventListener?.('abort', onCancel);
        }
      };

      let response = await fetchOnce();
      if (response.status === 503 || response.status === 429) {
        console.log(`[CacheService] Transient ${response.status}, retrying in 1.5s…`);
        await new Promise(r => setTimeout(r, 1500));
        response = await fetchOnce();
      }
      
      const text = await response.text();
      
      if (!response.ok) {
        throw new Error(`API Error: ${text}`);
      }
      
      const data = JSON.parse(text);
      
      // ═══════════════════════════════════════════════════════════════════════════════
      // RESPONSE NORMALIZATION: Ensure consistent format for ALL frontend consumers
      // Backend may return: { results: [{ items: [...] }] } OR { odds: [...] }
      // Normalize to always have results[0].items as an array of odds objects
      // ═══════════════════════════════════════════════════════════════════════════════
      if (data.odds && Array.isArray(data.odds) && (!data.results || !data.results[0]?.items?.length)) {
        data.results = [{ items: data.odds }];
        console.log(`[CacheService] 🔄 Normalized: odds[] → results[0].items (${data.odds.length} items)`);
      }
      if (data.results?.[0]?.items) {
        const flatItems = [];
        for (const item of data.results[0].items) {
          if (item.odds && Array.isArray(item.odds)) {
            flatItems.push(...item.odds);
          } else {
            flatItems.push(item);
          }
        }
        if (flatItems.length !== data.results[0].items.length) {
          data.results[0].items = flatItems;
          console.log(`[CacheService] 🔄 Flattened nested odds → ${flatItems.length} items`);
        }
      }
      
      // Check if backend returned cached data
      const wasBackendCached = data.cached === true || data.fromCache === true || data.fastPath === true;
      
      if (onCacheStatus) onCacheStatus({ 
        stage: wasBackendCached ? 'backend_cache_hit' : 'live_api_complete', 
        message: wasBackendCached ? `Found ${data.results?.[0]?.items?.length || 0} results` : 'Search complete' 
      });
      
      console.log('[CacheService] ⚡ nlOdds returned:', wasBackendCached ? 'CACHED' : 'LIVE', '- items:', data.results?.[0]?.items?.length || 0);
      
      return {
        success: true,
        cached: wasBackendCached,
        ...data,
        entities
      };
      
    } catch (error) {
      // User pressed Cancel: surface a plain AbortError (no "timed out" text)
      // so the caller exits quietly instead of showing a timeout message.
      if (abortSignal?.aborted) {
        const cancelled = new Error('Search cancelled');
        cancelled.name = 'AbortError';
        throw cancelled;
      }
      console.error('[CacheService] Backend error:', error);
      
      // ⭐ FIX: Catch AbortError and provide a user-friendly message instead of
      // the cryptic browser default "signal is aborted without reason"
      const isAbortError = error?.name === 'AbortError' || 
        error?.message?.includes('aborted') || 
        error?.message?.includes('abort');
      
      const friendlyMessage = isAbortError 
        ? 'Query timed out — the search took too long. Try a more specific query (e.g. add a team name or market type).'
        : error.message;
      
      if (onCacheStatus) onCacheStatus({ 
        stage: 'error', 
        message: `Error: ${friendlyMessage}` 
      });
      
      // Re-throw with friendly message
      const friendlyError = new Error(friendlyMessage);
      friendlyError.name = error.name;
      friendlyError.originalError = error;
      throw friendlyError;
    }
}

/* OLD CODE BELOW - bypassed by direct nlOdds call above
    console.log('[CacheService] Straight bet query - checking odds cache...');
    
    // ✅ Use the market already parsed by parseQueryEntities (more reliable)
    // Only extract from prompt if parser didn't find a market
    let marketSearch = entities.market || market;
    
    if (!marketSearch) {
      // Fallback: Extract market keywords by removing team/player names from prompt
      // This handles "warriors 3rd quarter team total" → "3rd quarter team total"
      marketSearch = prompt.toLowerCase();
      
      // Remove team name if detected (use FULL query match to avoid partial removal)
      if (entities.team) {
        // Try to match full team name variations (e.g., "Los Angeles Clippers", "LA Clippers", "Clippers")
        const teamVariations = [
          entities.team,
          `los angeles ${entities.team}`,
          `la ${entities.team}`,
          `golden state ${entities.team}`,
          `new york ${entities.team}`
        ];
        for (const variation of teamVariations) {
          const teamPattern = new RegExp(`\\b${variation.replace(/\s+/g, '\\s+')}\\b`, 'gi');
          marketSearch = marketSearch.replace(teamPattern, '').trim();
        }
      }
      
      // Remove player name if detected
      if (entities.player) {
        const playerPattern = new RegExp(`\\b${entities.player.replace(/\s+/g, '\\s+')}\\b`, 'gi');
        marketSearch = marketSearch.replace(playerPattern, '').trim();
      }
      
      // Clean up extra whitespace
      marketSearch = marketSearch.replace(/\s+/g, ' ').trim();
    }
    
    console.log(`[CacheService] 📊 Market search: "${marketSearch}" (from ${entities.market ? 'parser' : 'prompt extraction'})`);
    
    // 🆕 Use league/sport detected from team name mapping
    // No more defaulting to NBA/basketball - let the AI detect unknown leagues
    const detectedLeague = entities.league || league;
    const detectedSport = entities.sport || sport;
    
    const searchParams = {
      league: detectedLeague || undefined,  // No default - let AI detect if unknown
      sport: detectedSport || undefined,    // No default - let AI detect if unknown
      player: entities.player || player,
      team: entities.team || team,
      market: marketSearch,  // Use parsed or extracted market
      maxAgeMinutes
    };
    
    // Log if we couldn't detect league for a team query
    if (entities.team && !detectedLeague) {
      console.log(`[CacheService] ⚠️ Unknown team "${entities.team}" - no league mapping, AI will detect`);
    }
    
    console.log('[CacheService] 🔍 Search params:', searchParams);
    
    const oddsResult = await getCachedOdds(searchParams);
    
    if (oddsResult.cached && oddsResult.odds?.length > 0) {
      console.log(`[CacheService] ⚡ Odds cache HIT - found ${oddsResult.odds.length} cached odds`);
      
      // 📊 Post-process: Filter by line if specified
      let filteredOdds = oddsResult.odds;
      
      if (entities.line !== null) {
        console.log(`[CacheService] 🎯 Filtering by line: ${entities.direction} ${entities.line}`);
        filteredOdds = oddsResult.odds.filter(odd => {
          const oddLine = odd.selection_line || odd.line || odd.points;
          if (oddLine === null || oddLine === undefined) return false;
          
          const numericLine = parseFloat(oddLine);
          if (isNaN(numericLine)) return false;
          
          // Match lines within 0.5 of target
          return Math.abs(numericLine - entities.line) <= 0.5;
        });
        
        console.log(`[CacheService] 📊 After line filter: ${filteredOdds.length} odds`);
      }
      
      // ⭐⭐⭐ STRICT TEAM FILTERING: When user searches for a specific team, ONLY show that team's bets
      // Filter out opponent bets - this matches backend filtering logic
      if (entities.team && filteredOdds.length > 0) {
        const searchedTeam = entities.team.toLowerCase();
        const beforeCount = filteredOdds.length;
        
        filteredOdds = filteredOdds.filter(odd => {
          const selection = (odd.selection || odd.name || '').toLowerCase();
          const market = (odd.market || odd.market_id || '').toLowerCase();
          
          // Allow pure total markets (Over/Under without team name)
          const isTotalMarket = (market.includes('total') && !market.includes('+')) || 
                                market.includes('over/under') || 
                                market.includes('o/u');
          if (isTotalMarket && /\b(over|under)\b/i.test(selection)) {
            return true;
          }
          
          // Allow 3-way moneyline (Draw option)
          if (selection === 'draw' || selection === 'tie') {
            return true;
          }
          
          // Check if selection contains the searched team name
          return selection.includes(searchedTeam);
        });
        
        if (beforeCount > filteredOdds.length) {
          console.log(`[CacheService] ⭐ STRICT TEAM FILTER: ${beforeCount} → ${filteredOdds.length} items for "${entities.team}" (opponent bets removed)`);
        }
      }
      
      // 🎯 Post-process: Relevance-based sorting
      if (entities.market && filteredOdds.length > 0) {
        console.log(`[CacheService] 📊 Sorting by market relevance: ${entities.market}`);
          const targetMarket = entities.market.toLowerCase();
          
        // Score-based relevance function
        const getMarketRelevanceScore = (odd) => {
          const market = (odd.market || odd.market_id || '').toLowerCase();
          
          // Score 100: Exact match (e.g., "Moneyline" for moneyline query)
          if (market === targetMarket || market === `player ${targetMarket}`) return 100;
          
          // Score 90: Starts with target market
          if (market.startsWith(targetMarket)) return 90;
          
          // Score 80: Plain version (no qualifiers like "1st half", "1st qt")
          const hasQualifier = /\d|half|quarter|qt|qtr|period|inning/i.test(market);
          if (market.includes(targetMarket) && !hasQualifier) return 80;
          
          // Score 50: Contains target market with qualifiers
          if (market.includes(targetMarket)) return 50;
          
          // Score 0: Doesn't match
          return 0;
        };
        
        filteredOdds = filteredOdds.sort((a, b) => {
          const aRelevance = getMarketRelevanceScore(a);
          const bRelevance = getMarketRelevanceScore(b);
          
          // Primary: Sort by relevance (higher first)
          if (aRelevance !== bRelevance) {
            return bRelevance - aRelevance;
          }
          
          // Secondary: Sort by EV% (higher first)
          const aEV = a.ev_percent || 0;
          const bEV = b.ev_percent || 0;
          return bEV - aEV;
        });
        
        console.log(`[CacheService] ✅ Sorted by relevance (exact:100 → starts:90 → plain:80 → qualified:50 → EV%)`);
      }
      
      if (filteredOdds.length > 0) {
        cacheResult = {
          cached: true,
          source: 'odds_cache',
          odds: filteredOdds,
          entities // Include parsed entities for client-side use
        };
      } else {
        console.log('[CacheService] ⚠️ No odds matched filter criteria');
      }
    }
  
  // Step 2: If cache hit, return cached data
  if (cacheResult && cacheResult.cached) {
    if (onCacheStatus) onCacheStatus({ 
      stage: 'cache_hit', 
      message: 'Using cached data' 
    });
    
    console.log('[CacheService] Cache HIT - returning cached data');

    const _queryClassification = synthesizeClassification(entities);
    const _queryResolution = buildQueryResolution(entities, prompt);

    return {
      success: true,
      cached: true,
      source: cacheResult.source,
      _queryClassification,
      _queryResolution,
      _backendSorted: false,
      results: [{
        items: cacheResult.odds || [],
        endpoint: 'cache',
        query: { ...entities }
      }],
      entities
    };
  }
  
  // Step 3: Cache miss - fall back to live API
  console.log('[CacheService] Cache MISS - falling back to live API');
  
  if (onCacheStatus) onCacheStatus({ 
    stage: 'cache_miss', 
    message: 'Searching for best odds...' 
  });
  
  try {
    const base = getFunctionsBaseUrl();
    
    // ⚠️ Note: The backend may still return cached data even on frontend "cache miss"
    // Only show "live API" message if backend actually calls OpticOdds API
    
    // 🔧 Override prefer to 'straight' for player prop queries
    const effectivePrefer = isPlayerPropQuery ? 'straight' : prefer;
    
    const body = {
      prompt,
      prefer: effectivePrefer,
      ...(isLive && { isLive: true }),
      defaults: {
        ...defaults,
        sport,
        league: league?.toLowerCase(),
        timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
        now: new Date().toISOString()
      }
    };
    
    console.log('[CacheService] Calling /nlOdds with:', body);
    if (isPlayerPropQuery) {
      console.log('[CacheService] 🎯 Overrode prefer from', prefer, 'to', effectivePrefer, 'for player prop query');
    }
    
    const response = await fetch(`${base}/nlOdds`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body)
    });
    
    const text = await response.text();
    
    if (!response.ok) {
      throw new Error(`API Error: ${text}`);
    }
    
    const data = JSON.parse(text);
    
    // Check if backend returned cached data or had to hit live API
    const wasBackendCached = data.cached === true || data.fromCache === true;
    
    if (onCacheStatus) onCacheStatus({ 
      stage: wasBackendCached ? 'backend_cache_hit' : 'live_api_complete', 
      message: wasBackendCached ? 'Found data' : 'Search complete' 
    });
    
    console.log('[CacheService] nlOdds returned:', wasBackendCached ? 'BACKEND CACHE' : 'LIVE API');
    
    return {
      success: true,
      cached: wasBackendCached,
      ...data
    };
    
  } catch (error) {
    console.error('[CacheService] Live API error:', error);
    
    if (onCacheStatus) onCacheStatus({ 
      stage: 'error', 
      message: `Error: ${error.message}` 
    });
    
    throw error;
  }
}
END OF OLD CODE - bypassed */

/**
 * Check if cache is healthy and available
 * @returns {Promise<{healthy: boolean, age_minutes: number}>}
 */
export async function checkCacheHealth() {
  try {
    const stats = await getCacheStats();
    
    const isHealthy = stats.success && 
                     stats.cache_status === 'active' && 
                     stats.age_minutes < 120; // Cache should be less than 2 hours old
    
    return {
      healthy: isHealthy,
      age_minutes: stats.age_minutes || 999,
      stats
    };
  } catch (error) {
    return {
      healthy: false,
      age_minutes: 999,
      error: error.message
    };
  }
}

/**
 * ⚡ Query SGP cache for pre-generated SGPs
 * @param {string} fixtureId - Fixture ID to look up
 * @returns {Promise<{success: boolean, cached: boolean, sgps: Object, fixture: Object}>}
 */
export async function querySGPCache(fixtureId) {
  try {
    console.log('[CacheService] Checking SGP cache for fixture:', fixtureId);
    
    // Import Firebase here to avoid circular dependencies
    const { db } = await import('../firebaseConfig');
    const { doc, getDoc } = await import('firebase/firestore');
    
    const sgpDoc = await getDoc(doc(db, 'sgp_cache', fixtureId));
    
    if (!sgpDoc.exists()) {
      console.log('[CacheService] No SGP cache found for fixture');
      return {
        success: true,
        cached: false,
        sgps: null,
        message: 'No cached SGPs for this fixture'
      };
    }
    
    const data = sgpDoc.data();
    
    // Check if cache is expired
    const generatedAt = data.generated_at?.toDate?.() || new Date(data.generated_at);
    const expiresAt = data.expires_at?.toDate?.() || new Date(data.expires_at);
    const now = new Date();
    
    if (now > expiresAt) {
      console.log('[CacheService] SGP cache expired');
      return {
        success: true,
        cached: false,
        sgps: null,
        message: 'SGP cache expired'
      };
    }
    
    const cacheAgeMinutes = (now - generatedAt) / (1000 * 60);
    console.log(`[CacheService] SGP cache HIT (age: ${cacheAgeMinutes.toFixed(1)} mins)`);
    
    return {
      success: true,
      cached: true,
      sgps: data.sgps || {},
      stats: data.stats || {},
      fixture: {
        id: data.fixture_id,
        home_team: data.home_team,
        away_team: data.away_team,
        league: data.league,
        sport: data.sport,
        start_date: data.start_date
      },
      cache_age_minutes: cacheAgeMinutes
    };
  } catch (error) {
    console.error('[CacheService] Error querying SGP cache:', error);
    return {
      success: false,
      cached: false,
      sgps: null,
      error: error.message
    };
  }
}

/**
 * 🚀 Generate SGPs on-demand for a fixture
 * @param {string} fixtureId - Fixture ID
 * @param {Object} cachedOdds - Pre-fetched cached odds (optional)
 * @param {Function} onProgress - Progress callback (optional)
 * @returns {Promise<{success: boolean, sgps: Object, stats: Object}>}
 */
export async function generateSGPsOnDemand(fixtureId, cachedOdds = null, onProgress = null) {
  try {
    console.log('[CacheService] Generating SGPs on-demand for:', fixtureId);
    
    if (onProgress) onProgress({ stage: 'generating', message: 'Generating custom SGPs...' });
    
    const base = getFunctionsBaseUrl();
    const url = `${base}/generateSGPs`;
    
    const body = {
      fixture_id: fixtureId,
      cached_odds: cachedOdds, // Pass cached odds if available
      options: {
        maxCombinations: 500 // Use 500 limit for manual generation (fast)
      }
    };
    
    const response = await fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body)
    });
    
    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`HTTP ${response.status}: ${errorText}`);
    }
    
    const data = await response.json();
    
    if (!data.success) {
      throw new Error(data.error || 'SGP generation failed');
    }
    
    if (onProgress) onProgress({ stage: 'complete', message: 'SGPs generated!' });
    
    console.log('[CacheService] SGPs generated successfully:', {
      twoLeg: data.stats?.['2-leg']?.total || 0,
      threeLeg: data.stats?.['3-leg']?.total || 0
    });
    
    return {
      success: true,
      sgps: data.sgps || {},
      stats: data.stats || {},
      fixture: data.fixture || {}
    };
  } catch (error) {
    console.error('[CacheService] Error generating SGPs:', error);
    
    if (onProgress) onProgress({ stage: 'error', message: `Error: ${error.message}` });
    
    return {
      success: false,
      error: error.message
    };
  }
}

/**
 * ⚡ Smart SGP query with cache-first + on-demand generation
 * 
 * Flow:
 * 1. Check SGP cache → if found, return immediately ✅
 * 2. Check if fixture is in odds cache with enough markets → generate on-demand ✅
 * 3. Fall back to live API ⚠️
 * 
 * @param {Object} params - Query parameters
 * @param {string} params.fixtureId - Fixture ID (if known)
 * @param {string} params.prompt - Natural language query
 * @param {Object} params.options - Query options
 * @param {Function} onStatus - Status callback
 * @returns {Promise<Object>}
 */
export async function querySGPsWithCacheFallback(params, onStatus = null) {
  const { fixtureId, prompt, options = {} } = params;
  
  console.log('[CacheService] Smart SGP query:', params);
  
  // Step 1: If we have a fixture ID, check SGP cache directly
  if (fixtureId) {
    if (onStatus) onStatus({ stage: 'checking_sgp_cache', message: 'Checking for pre-generated SGPs...' });
    
    const sgpResult = await querySGPCache(fixtureId);
    
    if (sgpResult.cached && sgpResult.sgps) {
      console.log('[CacheService] SGP cache HIT!');
      if (onStatus) onStatus({ stage: 'sgp_cache_hit', message: 'Found pre-generated SGPs!' });
      
      return {
        success: true,
        cached: true,
        source: 'sgp_cache',
        sgps: sgpResult.sgps,
        stats: sgpResult.stats,
        fixture: sgpResult.fixture
      };
    }
    
    // Step 2: SGP not cached - check if we can generate on-demand
    console.log('[CacheService] SGP not cached, checking if generatable...');
    if (onStatus) onStatus({ stage: 'checking_generatable', message: 'Checking if SGPs can be generated...' });
    
    // Import Firebase here
    const { db } = await import('../firebaseConfig');
    const { doc, getDoc } = await import('firebase/firestore');
    
    const oddsDoc = await getDoc(doc(db, 'odds_cache', fixtureId));
    
    if (oddsDoc.exists()) {
      const oddsData = oddsDoc.data();
      const totalMarkets = oddsData.total_markets || 0;
      
      if (totalMarkets >= 10) {
        // Fixture has enough markets - generate SGPs on-demand!
        // Note: Backend will fetch odds from odds_markets (not odds_cache)
        console.log(`[CacheService] Fixture is generatable (${totalMarkets} markets) - generating now...`);
        if (onStatus) onStatus({ stage: 'generating_sgps', message: `Generating SGPs from ${totalMarkets} markets...` });
        
        // Pass null for cachedOdds - backend fetches from odds_markets
        const genResult = await generateSGPsOnDemand(
          fixtureId,
          null, // Backend fetches odds from odds_markets
          onStatus
        );
        
        if (genResult.success) {
          return {
            success: true,
            cached: false,
            generated: true,
            source: 'on_demand_generation',
            sgps: genResult.sgps,
            stats: genResult.stats,
            fixture: genResult.fixture
          };
        }
      } else {
        console.log(`[CacheService] Fixture not generatable (only ${totalMarkets} markets, need 10+)`);
      }
    }
  }
  
  // Step 3: Fall back to live API query
  console.log('[CacheService] Falling back to live API for SGP query');
  if (onStatus) onStatus({ stage: 'live_api', message: 'Performing live search...' });
  
  return await queryWithCacheFallback(prompt, options, onStatus);
}

// ═══════════════════════════════════════════════════════════════════════════════
// ⭐ AUTOCOMPLETE SUGGESTIONS - Fast in-memory entity lookups via /suggest endpoint
// ═══════════════════════════════════════════════════════════════════════════════

/**
 * Fetch autocomplete suggestions from the /suggest Cloud Function
 * @param {string} partial - User's current input text (minimum 2 chars)
 * @param {Object} options - Optional settings
 * @param {string} options.sport - Filter by sport (e.g., 'nba', 'nfl')
 * @param {number} options.limit - Max suggestions (default 8, max 15)
 * @param {AbortSignal} options.signal - AbortController signal for cancellation
 * @returns {Promise<Array>} Array of suggestion objects
 */
export async function fetchSuggestions(partial, options = {}) {
  if (!partial || partial.trim().length < 2) return [];

  try {
    const baseUrl = getFunctionsBaseUrl();
    const response = await fetch(`${baseUrl}/suggest`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        partial: partial.trim(),
        sport: options.sport || null,
        limit: options.limit || 8,
      }),
      signal: options.signal,
    });

    if (!response.ok) return [];
    const data = await response.json();
    return data.suggestions || [];
  } catch (err) {
    if (err.name === 'AbortError') return []; // Expected on rapid typing
    console.warn('[CacheService] Suggest error:', err.message);
    return [];
  }
}
